import type { FeatureCollection } from "geojson";
import type { LngLatBoundsLike } from "maplibre-gl";
import { theme } from "./theme";
import type { Stop } from "./types";

/**
 * Dataene 3D-kartet tegner, skilt ut fra kartkomponenten.
 *
 * Begge funksjonene her hadde en feil som ikke kunne fanges i test så lenge de
 * bodde inne i komponenten: jsdom har ingen WebGL, så kartet kan ikke kjøres,
 * og maplibre-testen måtte lese kildekoden som tekst. Som frie funksjoner kan
 * de prøves med vanlige påstander.
 */

/**
 * Fargene på de enkelte stedene rundt et stopp.
 *
 * Overnatting, lading, spisesteder og skianlegg ligger allerede i dataene med
 * ekte koordinater — de ble bare vist som en liste bak et klikk, og som ett
 * merke på stoppet. En turist som lurer på hvor hotellet faktisk *ligger* i
 * forhold til fjorden fikk ikke svar på det av «0,4 km».
 *
 * Fargene er de samme som tjenestemerkene bruker, så et blått punkt på kartet
 * og sengeikonet i forklaringen hører åpenbart sammen.
 *
 * ── Hvorfor to av dem er mørkere enn de var ────────────────────────────────
 *
 * Fargene er ikke bare punkter på kartet: en påslått chip i stedsfilteret
 * fylles med dem, og teksten på den er hvit. Målt der var `#6B7A80` 4,43:1 og
 * `#5A7FA6` 4,13:1 — begge under 4,5:1, som er kravet for skrift i denne
 * størrelsen. De øvrige klarte det.
 *
 * De to er derfor senket akkurat nok til å komme over, og ikke mer: drivstoff
 * skal fortsatt lese som grått og skianlegg som blått, både i chipen og på
 * kartet, siden det er nettopp den koblingen fargene finnes for.
 */
export const PLACE_COLOR: Record<PlaceKind, string> = {
  hotel: theme.color.fjord,
  hostel: theme.color.fjord,
  camping: "#3F7A5E",
  cabin: "#3F7A5E",
  charging: "#2F6F8F",
  fuel: "#5C6A70",
  food: "#8A6A2F",
  ski: "#4A6C8E",
};

export type PlaceKind =
  | "hotel"
  | "hostel"
  | "camping"
  | "cabin"
  | "charging"
  | "fuel"
  | "food"
  | "ski";

/**
 * Alle navngitte steder rundt stoppene på ruta, som punkter der de faktisk er.
 *
 * Bygget fra `lodging`, `amenities` og `ski.areas` på hvert stopp. Ingen nye
 * kall: dette er data appen alt har lastet ned for tidslinja.
 *
 * `kinds` er brukerens filter. Utelatt betyr alt — kartet skal virke uten at
 * noen har tatt et valg.
 */
export function placesOf(stops: Stop[], kinds?: Set<PlaceKind>): FeatureCollection {
  const features: FeatureCollection["features"] = [];
  const seen = new Set<string>();

  const add = (
    key: string,
    kind: PlaceKind,
    name: string,
    lat: number,
    lon: number,
    extra: Record<string, string | number> = {},
  ) => {
    // Typen er filtrert bort av brukeren.
    if (kinds && !kinds.has(kind)) return;
    // Samme hotell kan ligge nær to stopp og komme med i begge listene.
    if (seen.has(key)) return;
    seen.add(key);
    features.push({
      type: "Feature",
      properties: { kind, name, color: PLACE_COLOR[kind], ...extra },
      geometry: { type: "Point", coordinates: [lon, lat] },
    });
  };

  for (const stop of stops) {
    for (const entry of stop.lodging ?? []) {
      add(`l:${entry.id}`, entry.kind, entry.name, entry.lat, entry.lon, {
        detail: `${entry.distanceKm} km`,
        ...(entry.website ? { website: entry.website } : {}),
        ...(entry.dnt ? { badge: "DNT" } : {}),
      });
    }
    for (const entry of stop.amenities ?? []) {
      add(`a:${entry.id}`, entry.kind, entry.name, entry.lat, entry.lon, {
        detail: `${entry.distanceKm} km`,
      });
    }
    for (const area of stop.ski?.areas ?? []) {
      add(`s:${area.id}`, "ski", area.name, area.lat, area.lon, {
        detail: `${area.distanceKm} km`,
        ...(area.website ? { website: area.website } : {}),
      });
    }
  }

  return { type: "FeatureCollection", features };
}

/** Hele Norge, brukt når det ikke finnes stopp å ramme inn ennå. */
export const NORWAY: LngLatBoundsLike = [
  [4.5, 57.9],
  [31.2, 71.2],
];

/**
 * Rammen rundt et sett stopp.
 *
 * Minstestørrelsen er ikke pynt. `fitBounds` på en boks uten utstrekning —
 * ett stopp, eller to som ligger noen hundre meter fra hverandre — zoomer til
 * maksimalt nivå, og kartet endte på gatenivå i Oslo med ett synlig punkt der
 * ruta skulle vært. En halv grad er omtrent 55 km, nok til at man ser hvor man
 * er, og nok til at boksen aldri er degenerert.
 *
 * Tom liste gir Norge. Det er et ærlig svar på «vi vet ikke ennå», og langt
 * bedre enn `Math.min()` av ingenting, som er Infinity og gir et kart uten bilde.
 */
export function boundsOf(stops: Stop[]): LngLatBoundsLike {
  if (stops.length === 0) return NORWAY;

  const lons = stops.map((s) => s.lon);
  const lats = stops.map((s) => s.lat);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const MIN_SPAN = 0.5;
  const padLon = Math.max(0, (MIN_SPAN - (maxLon - minLon)) / 2);
  const padLat = Math.max(0, (MIN_SPAN - (maxLat - minLat)) / 2);

  return [
    [minLon - padLon, minLat - padLat],
    [maxLon + padLon, maxLat + padLat],
  ];
}
